import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reportingService } from '../services/ReportingService';
import {
  SessionStatisticsReport as SessionStatisticsReportData,
  RBTPerformanceReport as RBTPerformanceReportData,
  SupervisionComplianceReport as SupervisionComplianceReportData,
  BillingSummaryReport as BillingSummaryReportData,
  LocationVerificationReport as LocationVerificationReportData,
  TimesheetSummaryReport as TimesheetSummaryReportData,
  DateRange,
  ReportType,
} from '../models/Report';
import {
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  MapPin,
  Clock,
  Calendar,
  ArrowLeft,
} from 'lucide-react';
import { SessionStatisticsReport } from '../components/reports/SessionStatisticsReport';
import { RBTPerformanceReport } from '../components/reports/RBTPerformanceReport';
import { SupervisionComplianceReport } from '../components/reports/SupervisionComplianceReport';
import { BillingSummaryReport } from '../components/reports/BillingSummaryReport';
import { LocationVerificationReport } from '../components/reports/LocationVerificationReport';
import { TimesheetSummaryReport } from '../components/reports/TimesheetSummaryReport';

type TimePeriod = 'this-month' | 'last-30-days' | 'last-month' | 'this-year' | 'custom';
type MainTab = 'dashboard' | 'reports';
type ReportView = 'main' | ReportType;

const TIME_PERIODS: { value: TimePeriod; label: string }[] = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-year', label: 'This year' },
  { value: 'custom', label: 'Custom' },
];

function dateRangeFromPeriod(period: TimePeriod, current: DateRange): DateRange {
  if (period === 'custom') return current;
  const now = new Date();
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  let startDate = new Date();
  startDate.setHours(0, 0, 0, 0);

  switch (period) {
    case 'this-month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'last-30-days':
      startDate = new Date();
      startDate.setDate(now.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'last-month': {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      lastDay.setHours(23, 59, 59, 999);
      return { startDate, endDate: lastDay };
    }
    case 'this-year':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
  }
  return { startDate, endDate };
}

const REPORT_CARDS: Array<{
  type: ReportType;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  iconColor: string;
}> = [
  { type: 'session-statistics',     title: 'Session Statistics',     subtitle: 'Overall session metrics',   icon: BarChart3,  iconColor: 'var(--color-primary)' },
  { type: 'rbt-performance',        title: 'RBT Performance',        subtitle: 'Individual RBT metrics',    icon: TrendingUp, iconColor: 'var(--color-success)' },
  { type: 'supervision-compliance', title: 'Supervision Compliance', subtitle: 'Supervision tracking',      icon: Users,      iconColor: 'var(--color-info)' },
  { type: 'billing-summary',        title: 'Billing Summary',        subtitle: 'Billable hours & codes',    icon: DollarSign, iconColor: 'var(--color-warning)' },
  { type: 'location-verification',  title: 'Location Verification',  subtitle: 'GPS compliance',            icon: MapPin,     iconColor: 'var(--color-error)' },
  { type: 'timesheet-summary',      title: 'Timesheet Summary',      subtitle: 'Payroll overview',          icon: Clock,      iconColor: 'var(--color-purple)' },
];

export const Reports: React.FC = () => {
  const { user } = useAuth();

  const [mainTab, setMainTab] = useState<MainTab>('dashboard');
  const [currentReport, setCurrentReport] = useState<ReportView>('main');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('last-30-days');

  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Date range — driven by timePeriod, except when 'custom'
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    dateRangeFromPeriod('last-30-days', {
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
    })
  );

  // Per-report data
  const [sessionStats, setSessionStats] = useState<SessionStatisticsReportData | null>(null);
  const [rbtPerformance, setRbtPerformance] = useState<RBTPerformanceReportData | null>(null);
  const [supervisionCompliance, setSupervisionCompliance] = useState<SupervisionComplianceReportData | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummaryReportData | null>(null);
  const [locationVerification, setLocationVerification] = useState<LocationVerificationReportData | null>(null);
  const [timesheetSummary, setTimesheetSummary] = useState<TimesheetSummaryReportData | null>(null);

  // RBT selection for performance report
  const [selectedRbtId, setSelectedRbtId] = useState<string>('');

  // When timePeriod changes (and isn't 'custom'), recompute dateRange
  useEffect(() => {
    if (timePeriod !== 'custom') {
      setDateRange((prev) => dateRangeFromPeriod(timePeriod, prev));
    }
  }, [timePeriod]);

  // Load a single report when its detail view is active
  const loadReport = useCallback(async (type: ReportType) => {
    if (!user?.companyId) return;
    setLoading(true);
    setError(null);
    try {
      switch (type) {
        case 'session-statistics':
          setSessionStats(await reportingService.generateSessionStatisticsReport(user.companyId, dateRange));
          break;
        case 'rbt-performance':
          if (selectedRbtId) {
            setRbtPerformance(await reportingService.generateRBTPerformanceReport(user.companyId, selectedRbtId, dateRange));
          }
          break;
        case 'supervision-compliance':
          setSupervisionCompliance(await reportingService.generateSupervisionComplianceReport(user.companyId, dateRange));
          break;
        case 'billing-summary':
          setBillingSummary(await reportingService.generateBillingSummaryReport(user.companyId, dateRange));
          break;
        case 'location-verification':
          setLocationVerification(await reportingService.generateLocationVerificationReport(user.companyId, dateRange));
          break;
        case 'timesheet-summary':
          setTimesheetSummary(await reportingService.generateTimesheetSummaryReport(user.companyId, dateRange));
          break;
      }
    } catch (err: any) {
      console.error('Error loading report:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, dateRange, selectedRbtId]);

  // Load core KPIs for the Dashboard tab in parallel
  const loadDashboard = useCallback(async () => {
    if (!user?.companyId) return;
    setDashboardLoading(true);
    setError(null);
    try {
      const [stats, supervision, location, billing] = await Promise.all([
        reportingService.generateSessionStatisticsReport(user.companyId, dateRange),
        reportingService.generateSupervisionComplianceReport(user.companyId, dateRange),
        reportingService.generateLocationVerificationReport(user.companyId, dateRange),
        reportingService.generateBillingSummaryReport(user.companyId, dateRange),
      ]);
      setSessionStats(stats);
      setSupervisionCompliance(supervision);
      setLocationVerification(location);
      setBillingSummary(billing);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setDashboardLoading(false);
    }
  }, [user?.companyId, dateRange]);

  // Drive data loading off the active view
  useEffect(() => {
    if (!user?.companyId) return;
    if (currentReport !== 'main') {
      loadReport(currentReport);
    } else if (mainTab === 'dashboard') {
      loadDashboard();
    }
  }, [user?.companyId, mainTab, currentReport, dateRange, selectedRbtId, loadReport, loadDashboard]);

  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    setTimePeriod('custom');
    setDateRange((prev) => ({ ...prev, [field]: new Date(value) }));
  };

  if (!user || (user.role !== 'admin' && user.role !== 'supervisor')) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <div className="alert alert-error">
          You do not have permission to access reports.
        </div>
      </div>
    );
  }

  const isViewingReport = currentReport !== 'main';

  return (
    <div
      className="container"
      style={{
        paddingTop: 'var(--spacing-8)',
        paddingBottom: 'var(--spacing-8)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}>
          {isViewingReport && (
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentReport('main')}
              aria-label="Back to Reports"
            >
              <ArrowLeft size={18} style={{ marginRight: 'var(--spacing-2)' }} />
              Back
            </button>
          )}
          <div>
            <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Reports</h1>
            <p style={{ color: 'var(--color-gray-600)' }}>
              View comprehensive reports and analytics for your organization
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs (Dashboard | Reports) — only on main view, not inside a specific report */}
      {!isViewingReport && (
        <div
          role="tablist"
          aria-label="Reports view"
          style={{
            display: 'flex',
            gap: 'var(--spacing-6)',
            borderBottom: '2px solid var(--color-gray-200)',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          {(['dashboard', 'reports'] as MainTab[]).map((tab) => {
            const isActive = mainTab === tab;
            return (
              <button
                key={tab}
                role="tab"
                aria-selected={isActive}
                onClick={() => setMainTab(tab)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 'var(--spacing-3) 0',
                  fontSize: 'var(--text-base)',
                  fontWeight: isActive ? 'var(--font-semibold)' : 'var(--font-normal)',
                  color: isActive ? 'var(--color-primary)' : 'var(--color-gray-600)',
                  cursor: 'pointer',
                  borderBottom: isActive ? '3px solid var(--color-primary)' : '3px solid transparent',
                  marginBottom: '-2px',
                  textTransform: 'capitalize',
                }}
              >
                {tab}
              </button>
            );
          })}
        </div>
      )}

      {/* Time-period filter row + custom date range — shown on main view (Dashboard or Reports list) */}
      {!isViewingReport && (
        <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-4)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
              {TIME_PERIODS.map((p) => {
                const isActive = timePeriod === p.value;
                return (
                  <button
                    key={p.value}
                    onClick={() => setTimePeriod(p.value)}
                    className={`btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {timePeriod === 'custom' && (
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
                <Calendar size={20} style={{ color: 'var(--color-gray-600)' }} />
                <input
                  type="date"
                  className="form-input"
                  value={dateRange.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                />
                <span>to</span>
                <input
                  type="date"
                  className="form-input"
                  value={dateRange.endDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-6)' }}>
          {error}
        </div>
      )}

      {/* Body */}
      {isViewingReport ? (
        loading ? (
          <div className="card" style={{ padding: 'var(--spacing-8)', textAlign: 'center' }}>
            <div>Loading report...</div>
          </div>
        ) : (
          <>
            {currentReport === 'session-statistics' && sessionStats && (
              <SessionStatisticsReport data={sessionStats} onBack={() => setCurrentReport('main')} />
            )}
            {currentReport === 'rbt-performance' && (
              <RBTPerformanceReport
                data={rbtPerformance}
                selectedRbtId={selectedRbtId}
                onSelectRbt={setSelectedRbtId}
                onBack={() => setCurrentReport('main')}
              />
            )}
            {currentReport === 'supervision-compliance' && supervisionCompliance && (
              <SupervisionComplianceReport data={supervisionCompliance} onBack={() => setCurrentReport('main')} />
            )}
            {currentReport === 'billing-summary' && billingSummary && (
              <BillingSummaryReport data={billingSummary} onBack={() => setCurrentReport('main')} />
            )}
            {currentReport === 'location-verification' && locationVerification && (
              <LocationVerificationReport data={locationVerification} onBack={() => setCurrentReport('main')} />
            )}
            {currentReport === 'timesheet-summary' && timesheetSummary && (
              <TimesheetSummaryReport data={timesheetSummary} onBack={() => setCurrentReport('main')} />
            )}
          </>
        )
      ) : mainTab === 'dashboard' ? (
        <DashboardTab
          loading={dashboardLoading}
          sessionStats={sessionStats}
          supervisionCompliance={supervisionCompliance}
          locationVerification={locationVerification}
          billingSummary={billingSummary}
          onOpenReport={(t) => setCurrentReport(t)}
        />
      ) : (
        <div
          className="grid grid-cols-3"
          style={{ gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}
        >
          {REPORT_CARDS.map(({ type, title, subtitle, icon: Icon, iconColor }) => (
            <button
              key={type}
              className="card"
              onClick={() => setCurrentReport(type)}
              style={{ cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                <Icon size={24} style={{ color: iconColor }} />
                <div>
                  <div style={{ fontWeight: 'var(--font-semibold)' }}>{title}</div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                    {subtitle}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Dashboard Tab — KPI tiles aggregating across reports.
 * Each tile has a "View report" link that navigates to the detail view.
 */
const DashboardTab: React.FC<{
  loading: boolean;
  sessionStats: SessionStatisticsReportData | null;
  supervisionCompliance: SupervisionComplianceReportData | null;
  locationVerification: LocationVerificationReportData | null;
  billingSummary: BillingSummaryReportData | null;
  onOpenReport: (t: ReportType) => void;
}> = ({ loading, sessionStats, supervisionCompliance, locationVerification, billingSummary, onOpenReport }) => {
  if (loading) {
    return (
      <div className="card" style={{ padding: 'var(--spacing-8)', textAlign: 'center' }}>
        <div>Loading dashboard...</div>
      </div>
    );
  }

  const tiles: Array<{
    label: string;
    value: string;
    sub?: string;
    color?: string;
    target: ReportType;
    icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
    iconColor: string;
  }> = [
    {
      label: 'Total Sessions',
      value: String(sessionStats?.totalSessions ?? '--'),
      sub: sessionStats ? `${sessionStats.completedSessions} completed` : undefined,
      target: 'session-statistics',
      icon: BarChart3,
      iconColor: 'var(--color-primary)',
    },
    {
      label: 'Total Hours',
      value: sessionStats ? `${sessionStats.totalSessionHours.toFixed(1)}` : '--',
      sub: sessionStats ? `Avg ${sessionStats.averageSessionDuration.toFixed(0)} min` : undefined,
      target: 'session-statistics',
      icon: Clock,
      iconColor: 'var(--color-purple)',
    },
    {
      label: 'Billable Hours',
      value: billingSummary ? `${billingSummary.totalBillableHours.toFixed(1)}` : '--',
      sub: billingSummary ? `${billingSummary.billingCodeBreakdown.length} codes` : undefined,
      color: 'var(--color-warning)',
      target: 'billing-summary',
      icon: DollarSign,
      iconColor: 'var(--color-warning)',
    },
    {
      label: 'Supervision Compliance',
      value: supervisionCompliance ? `${supervisionCompliance.complianceRate}%` : '--',
      sub: supervisionCompliance
        ? `${supervisionCompliance.supervisedSessions} of ${supervisionCompliance.totalSessionsRequiringSupervision}`
        : undefined,
      color: 'var(--color-success)',
      target: 'supervision-compliance',
      icon: Users,
      iconColor: 'var(--color-info)',
    },
    {
      label: 'Location Verification',
      value: locationVerification ? `${locationVerification.verificationRate}%` : '--',
      sub: locationVerification
        ? `${locationVerification.failedVerifications} flagged`
        : undefined,
      color: 'var(--color-success)',
      target: 'location-verification',
      icon: MapPin,
      iconColor: 'var(--color-error)',
    },
    {
      label: 'Pending Reviews',
      value: String(sessionStats?.pendingSessions ?? '--'),
      sub: sessionStats ? 'Awaiting supervisor' : undefined,
      target: 'session-statistics',
      icon: TrendingUp,
      iconColor: 'var(--color-success)',
    },
  ];

  return (
    <>
      {/* Projected stats row (placeholder — wired up when forecast data exists) */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-4)',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginBottom: 'var(--spacing-6)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-gray-50, #f9fafb)',
          borderRadius: 'var(--radius-md, 6px)',
          fontSize: 'var(--text-sm)',
          color: 'var(--color-gray-700)',
        }}
      >
        <span style={{ fontWeight: 'var(--font-semibold)' }}>Projected:</span>
        <span>-- scheduled sessions</span>
        <span>•</span>
        <span>-- supervised hours</span>
        <span>•</span>
        <span>-- billable hours</span>
      </div>

      <div className="grid grid-cols-3" style={{ gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}>
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Icon size={22} style={{ color: t.iconColor }} />
                <button
                  onClick={() => t.target && onOpenReport(t.target)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    padding: 0,
                  }}
                >
                  View report
                </button>
              </div>
              <div
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-gray-600)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                {t.label}
              </div>
              <div
                style={{
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 'var(--font-bold)',
                  color: t.color || 'inherit',
                  lineHeight: 1.1,
                }}
              >
                {t.value}
              </div>
              {t.sub && (
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-gray-500)',
                    marginTop: 'var(--spacing-2)',
                  }}
                >
                  {t.sub}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};
